import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BudgetSheetLink,
  SprintEntity,
  useBudgetSheet as useBudgetSheetLink,
  useSprintProductReportsBySprint,
  useSyncBudgetSheet,
} from '@data';

import { type BudgetTeamMember, readBudget, type VersionTeam, BudgetSection } from './budget';
import { budgetSheetPayload, type SheetSprint } from './budgetSheetPayload';
import { type FractionalPerson, readFractionalHours } from './fractional';
import { readQuarterCosts } from './quarterCosts';
import { readServiceHours } from './serviceLevels';
import { hoursByMember } from './spend';
import { useProductViewer } from './productViewer';

interface Source {
  productId: string;
  productName: string;
  year: string;
  /** The year's sprints as the API has them, because the reports are fetched against them. */
  sprints: SprintEntity[];
  groups: { label: string; members: BudgetTeamMember[] }[];
  teams: VersionTeam[];
  roster: FractionalPerson[];
  closedSprintIds: string[];
  /** Whether the settled list is still coming: without it every sprint reads as open. */
  settledLoading?: boolean;
  /** Whether that list failed outright, which reads the same way and must not be written either. */
  settledFailed?: boolean;
  /** Asking for it again, so the note the refusal puts on screen has something to retry. */
  settledRetry?: () => void;
  /** Bumped by whichever tab saved, so the figures are reread rather than the first ones seen. */
  revision: number;
}

export interface SheetControls {
  sheet: BudgetSheetLink | null | undefined;
  isSyncing: boolean;
  /** Nothing worth writing yet: the figures are still arriving, or the settled list never came. */
  isLoading: boolean;
  failed: boolean;
  /** The figures never arrived, rather than the sheet refusing: a different sentence to the reader. */
  figuresFailed: boolean;
  sync: () => void;
}

/** Both tabs build it here rather than each sending what it holds, or the last press would win. */
export const useProductBudgetSheet = (source: Source): SheetControls => {
  const { t } = useTranslation();
  const { productId, year, sprints, closedSprintIds, settledLoading, settledFailed, settledRetry, revision } = source;

  const { isClient } = useProductViewer();
  const { data: sheet } = useBudgetSheetLink(isClient ? '' : productId, Number(year));
  const mutation = useSyncBudgetSheet(productId);

  // Only the settled sprints are read below, and a 26-sprint year otherwise issued 26 requests on mount.
  const closedSprints = useMemo(
    () => sprints.filter(sprint => closedSprintIds.includes(sprint.id)),
    [sprints, closedSprintIds],
  );
  const { bySprint, isLoading: reportsLoading } = useSprintProductReportsBySprint(
    isClient ? [] : closedSprints,
    productId,
  );
  // A settled list still coming, or refused, reads as every sprint open: a closed year would go out as a plan.
  const isLoading = !!settledLoading || reportsLoading || !!settledFailed;

  /** What the workbook needs of a sprint: its number, when it starts, and nothing else. */
  const columns = useMemo<SheetSprint[]>(
    () => sprints.map(sprint => ({ id: sprint.id, number: sprint.number, startDate: sprint.startDate })),
    [sprints],
  );

  /** Reported where the sprint is settled, budgeted where it is still a plan. */
  const hours = useMemo(() => {
    const isClosed = (sprintId: string): boolean => closedSprintIds.includes(sprintId);
    return Object.fromEntries(
      columns.map(sprint => {
        const kind = isClosed(sprint.id) ? 'spend' : 'budget';
        const people = isClosed(sprint.id)
          ? hoursByMember(bySprint[sprint.id] ?? [])
          : (readBudget(productId, sprint.id)?.hours ?? {});
        return [sprint.id, { ...people, ...readFractionalHours(kind, productId, sprint.id) }];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, columns, closedSprintIds, bySprint, revision]);

  /** Per quarter, not per section: one Q1 actual must not erase the plan for Q2 to Q4. */
  const costs = useMemo(() => {
    const planned = readQuarterCosts('budget', productId, year);
    const paid = readQuarterCosts('spend', productId, year);

    return Object.fromEntries(
      Object.keys(planned).map(section => {
        const plannedLines = planned[section as BudgetSection] ?? [];
        const paidLines = paid[section as BudgetSection] ?? [];
        const merged = plannedLines.map(line => {
          const actual = paidLines.find(each => each.id === line.id || each.name === line.name);
          if (!actual) return line;
          return { ...line, amounts: { ...line.amounts, ...actual.amounts } };
        });
        // A line that only exists as something paid still belongs in the workbook.
        const extra = paidLines.filter(each => !merged.some(line => line.id === each.id || line.name === each.name));
        return [section, [...merged, ...extra]];
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, year, revision]);

  const sync = (): void => {
    // The retry on a refused settled list asks for that list again: writing is what there is no answer for.
    if (settledFailed) {
      settledRetry?.();
      return;
    }
    if (isLoading) return;
    mutation.mutate(
      budgetSheetPayload({
        year: Number(year),
        productName: source.productName,
        sprints: columns,
        closedSprintIds,
        groups: source.groups,
        fractional: source.roster,
        hours,
        teams: source.teams,
        costs,
        service: readServiceHours(productId),
        sectionLabel: section => t(`budget.sections.${section}`),
      }),
    );
  };

  const isSyncing = mutation.isPending;

  /** Off `revision`, not from inside the save: a push in the same breath sends the older figures. */
  const pushed = useRef(revision);
  useEffect(() => {
    if (revision === pushed.current) return;
    // Undefined is the lookup still in flight, which is not the same as having no workbook.
    if (sheet === undefined) return;
    // Held while one is being made, or the save that came with the press is marked and never written.
    if (!sheet) {
      if (isSyncing) return;
      pushed.current = revision;
      return;
    }
    // Held, not marked: `sync` refuses while the figures are arriving, and a save seen now is lost.
    if (isLoading) return;
    pushed.current = revision;
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision, sheet, isLoading, isSyncing]);

  // The mutation is keyed on the product, so without this a failed sync follows the reader into next year.
  const { reset } = mutation;
  useEffect(() => reset(), [year, reset]);

  return {
    sheet,
    isSyncing,
    isLoading,
    failed: mutation.isError || !!settledFailed,
    figuresFailed: !!settledFailed,
    sync,
  };
};
